<template>
  <div class="fullscreen modal">
    <div class="close" @click="close()">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="35"
        height="35"
        viewBox="0 0 35 35"
      >
        <g fill-rule="evenodd">
          <path
            d="M-5.147 15.441L40.147 15.441 40.147 19.559 -5.147 19.559z"
            transform="rotate(-45 17.5 17.5)"
          />
          <path
            d="M-5.147 15.441L40.147 15.441 40.147 19.559 -5.147 19.559z"
            transform="rotate(-135 17.5 17.5)"
          />
        </g>
      </svg>
    </div>

    <div
      :class="{
        'previous-image': true,
        visible: previousIndex !== sideImageIndex
      }"
      @click="goToPreviousImage()"
    >
      <NextButton />
    </div>
    <div class="ImageView">
      <div class="image-container">
        <div class="image-container-overlay" />
        <ProgressiveImage
          :maxres="1600"
          :filename="
            'https://media.getty.edu/iiif/image/' + $route.params.image
          "
        />
      </div>
      <div class="info">
        <div class="caption-line">
          <div class="caption" v-if="image">
            <span class="year">{{ image.year }}</span> | © Ed Ruscha.
            <a :href="imageGettyUrl" target="_blank"
              >View record at Getty Research Institute</a
            >.
          </div>
          <div class="share-wrapper">
            <div
              class="share"
              @click="$store.commit('setShare', !$store.state.ui.share)"
            >
              <div>
                Share
              </div>
            </div>
            <div class="sharecontainer">
              <ShareModal
                v-if="$store.state.ui.share"
                terminology="Photograph"
              />
            </div>
          </div>
        </div>
        <div
          class="stripmap"
          @click="goToImageMapView"
          v-if="!$store.state.isMobile"
        >
          <StripMap
            :locations="imageLocations"
            circleColor="#c04323"
            :invert="false"
          />
        </div>
        <div class="tag-outer" v-if="!$store.state.isMobile">
          <div class="tags">
            <div class="label">Visual tags</div>
            <div class="tag-inner">
              <div
                class="tag chip"
                v-for="tag in imageVisualTags"
                :key="tag + 'tag'"
                @click="goToTag(tag)"
              >
                <div class="chip-interior">
                  {{ tag.toLowerCase() }}
                </div>
              </div>
            </div>
          </div>
          <div
            class="tags"
            v-if="imageOcr.filter(ocr => ocr.length > 3).length > 0"
          >
            <div class="label">Recognized text</div>
            <div class="tag-inner">
              <div
                class="ocr chip"
                v-for="ocr in imageOcr
                  .filter(ocr => ocr.length > 3)
                  .map(o => o.toLowerCase())"
                :key="ocr + 'ocr'"
                @click="goToOcr(ocr)"
              >
                <div class="chip-interior">
                  {{ ocr }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="{ 'next-image': true, visible: nextIndex !== sideImageIndex }"
      @click="goToNextImage()"
    >
      <NextButton />
    </div>
  </div>
</template>

<script>
import NextButton from "../assets/next.svg?inline";
import ProgressiveImage from "../components/ProgressiveImage";
import StripMap from "../components/StripMap";
import ShareModal from "../components/ShareModal";
import { shootingDates } from "../constants/constants.js";

export default {
  name: "ImageView",
  components: {
    NextButton,
    ProgressiveImage,
    StripMap,
    ShareModal
  },
  mounted() {
    window.addEventListener("keydown", this.keydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.keydown);
  },
  computed: {
    imageLocations() {
      if (!this.image) return [];
      return [
        {
          id: this.image.id,
          position: this.image.location / this.$store.getters.totalDistance
        }
      ];
    },

    image() {
      let image = this.$store.getters.flattenedMappedImages.filter(
        i => i.id === this.$route.params.image
      );

      return image[0];
    },
    imageVisualTags() {
      if (!this.image) return [];
      return this.getUniqueSortedTags(this.image.tags);
    },
    imageOcr() {
      if (!this.image) return [];
      return this.getUniqueSortedTags(this.image.ocr);
    },
    imageGettyUrl() {
      return `https://www.getty.edu/research/collections/lookup/imageUUID?id=${this.image.id}`;
    },
    sideImages() {
      if (!this.image || !this.$store.getters.mappedImages) return [];

      const side = this.image.north ? "north" : "south";
      return this.$store.getters.mappedImages[this.image.year][side];
    },
    sideImageIndex() {
      if (!this.image || !this.sideImages) return null;
      return this.sideImages.map(i => i.location).indexOf(this.image.location);
    },
    previousIndex() {
      let previousIndex = this.sideImageIndex - 1;
      if (previousIndex < 0) {
        previousIndex = 0;
      }
      return previousIndex;
    },
    nextIndex() {
      let nextIndex = this.sideImageIndex + 1;
      if (nextIndex >= this.sideImages.length) {
        nextIndex = this.sideImages.length - 1;
      }
      return nextIndex;
    }
  },

  methods: {
    goToImageMapView() {
      this.$store.commit(
        "setLocation",
        this.image.location / this.$store.getters.totalDistance
      );

      if (!this.image.north) {
        this.$store.commit("setFlip", { flip: true, demo: false });
      } else {
        this.$store.commit("setFlip", { flip: false, demo: false });
      }

      this.$router.push({
        name: "map",
        params: { year: this.image.year },
        query: this.$route.query
      });
    },
    goToPreviousImage() {
      if (this.previousIndex === this.sideImageIndex) return;
      this.$router.push({
        name: this.$route.name,
        params: { image: this.sideImages[this.previousIndex].id },
        query: this.$route.query
      });
    },
    goToNextImage() {
      if (this.nextIndex === this.sideImageIndex) return;
      this.$router.push({
        name: this.$route.name,
        params: { image: this.sideImages[this.nextIndex].id },
        query: this.$route.query
      });
    },
    goToTag(tag) {
      this.$store.commit("setFilters", {
        filters: [{ category: "tag", value: tag.toLowerCase() }],
        pushToURL: { filters: ["tag," + tag.toLowerCase()] }
      });
      this.$router.push({ name: "contact-sheet", query: this.$route.query });
    },
    goToOcr(ocr) {
      this.$store.commit("setFilters", {
        filters: [{ category: "ocr", value: ocr }],
        pushToURL: { filters: ["ocr," + ocr] }
      });
      this.$router.push({ name: "contact-sheet", query: this.$route.query });
    },
    getUniqueSortedTags(tags) {
      return [...new Set(tags.map(tag => tag.toLowerCase()))].sort((a, b) =>
        a.localeCompare(b)
      );
    },
    keydown(e) {
      if (e.code === "Escape") {
        this.close();
      } else if (e.code === "ArrowLeft") {
        if (this.previousIndex) {
          this.goToPreviousImage();
        }
      } else if (e.code === "ArrowRight") {
        if (this.nextIndex) {
          this.goToNextImage();
        }
      }
    },
    close() {
      let upperPath = this.$route.path.split("/");

      if (upperPath.length > 0) {
        upperPath.splice(upperPath.length - 2);
        this.$router.push({
          path: upperPath.join("/"),
          query: this.$route.query
        });
      }
    },
    getShootingDate(year) {
      return shootingDates[year] || "";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/constants/_variables.scss";

.fullscreen {
  width: 100vw;
  height: 100vh;
  top: 0px;
  left: 0px;
  background-color: rgba(0, 0, 0, 0.8);
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10001; /* If tour is running, ensure modal is on top */
}

.next-image,
.previous-image {
  cursor: pointer;
  visibility: hidden;

  svg path {
    fill: white;
  }

  &:hover {
    svg path {
      fill: $light-gold;
    }
  }

  &.visible {
    visibility: unset;
  }
}

.next-image {
  margin-right: 2rem;
}

.previous-image {
  margin-left: 2rem;
  transform: rotate(180deg);
}

.close {
  font-size: 72px;
  width: 72px;
  height: 72px;
  cursor: pointer;
  color: white;
  position: absolute;
  top: 18px;
  right: 18px;

  display: flex;
  justify-content: center;
  align-items: center;
  path {
    fill: white;
  }

  &:hover {
    path {
      fill: $copper !important;
    }
  }
}

.ImageView {
  width: 90%;
  max-width: calc(0.95 * 100vh);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 1em;
  position: relative;

  @media (orientation: landscape) and (max-height: 815px) {
    max-width: 100%;
  }

  .image-container {
    width: 100%;
    margin-bottom: 0px;
    padding-bottom: 0px;
    position: relative;

    @media (orientation: landscape) and (max-height: 815px) {
      width: calc(0.95 * 100vh);
      height: calc(0.6 * 100vh);
    }

    &-overlay {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
    }

    img {
      width: 100%;
      max-height: 100%;
      object-fit: contain;
      margin-bottom: 0px;
      padding-bottom: 0px;
    }
  }

  .location {
    background-color: white;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    cursor: pointer;

    img {
      height: 80px;
    }
  }

  .stripmap {
    width: 100%;
    margin-bottom: 0.5em;
    cursor: pointer;
  }

  .info {
    color: white;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    text-align: left;
    max-width: 100vh;
    width: 100%;
    align-items: flex-start;

    .caption-line {
      align-items: center;
      padding: 10px;
      margin-top: -5px;
      background: black;
      display: flex;
      justify-content: space-between;
      width: 100%;

      @media (max-width: $sm-mobile-screen-width) {
        flex-direction: column;
      }
    }

    .caption {
      font-size: 15px;

      a {
        color: white;

        &:hover {
          color: $light-gold;
        }
      }
    }

    .tag-outer {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
    }

    .tags {
      display: flex;
      flex-grow: 1;
      align-items: center;

      .chip {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
      }

      .label {
        color: white;
        margin-right: 0.5em;
        font-weight: 500;
        text-transform: uppercase;
        width: 150px;
        flex-shrink: 0;
      }

      .tag-inner {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        flex-grow: 1;
      }
    }
  }
}

.share-wrapper {
  display: flex;
  flex-direction: column;

  .sharecontainer {
    position: relative;

    .ShareModal {
      width: 300px;
    }
  }

  @media (max-width: $mobile-screen-width) {
    margin-top: 0.5rem;
  }
}

.share {
  color: white;
  background-color: $copper;
  padding: 0.25em 0.5em;
  font-size: 1em;
  font-weight: 500;
  position: relative;

  font-style: italic;
  text-transform: uppercase;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: $light-gold;
    color: $copper;
  }
}

.year {
  font-weight: 500;
}
</style>

<style lang="scss">
@import "@/constants/_variables.scss";

.ImageView {
  .sharecontainer {
    position: relative;

    .ShareModal {
      @media (max-width: $mobile-screen-width) {
        font-size: 0.8em;
        top: -90px;
        width: 350px;
      }

      @media (max-width: $sm-mobile-screen-width) {
        font-size: 0.8em;
        right: -50px;
        top: 0px;
        width: 250px;
      }
    }
  }
}
</style>
